<script lang="ts">
	//Hooks
	import { afterUpdate, beforeUpdate, onMount } from 'svelte';

	//Helpers
	import {
		addToRequestLockedList,
		removeFromRequestLockedList,
		cloudGroupRequest,
		groupSimilarFinder,
	} from './control_group_helpers';

	//Stores
	import { user_settings } from 'src/stores/account/user';
	import { req_locked_devices } from 'src/stores/device-runtime/local-auth';
	import {
		createMemberSettingsSnapshot,
		createMemberStatusSnapshot,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';

	//Design system components
	import {
		ActionsContainer,
		DeviceButton,
		DoubleDeviceButton,
		Text,
		Icon,
		ControlsContainer,
		Alert,
		DeviceButtonV2,
	} from '@Shelly/shelly-design-system';

	import type { Device } from '../../../types/Device';
	import type { Group } from '../../../types/Group';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	import { hasGroupControlRights } from 'src/helpers/user_helpers';
	import {
		celsius_to_fahrenheit,
		fahrenheit_to_celsius,
		isBluTRV,
		isWallDisplay,
		real_id,
	} from '../../../helpers/interface_helpers';
	import {
		getTemplateTargetTemp,
		getTemplateTempUnit,
	} from 'src/helpers/xt1_helpers/template_units_helpers';
	import {
		getTrvComponentByDevice,
		isBluTRVCalibrated,
	} from 'src/helpers/virtual_helpers/blutrv_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { format } from 'svelte-i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';

	export let variant: 'listMenuCard' | 'widgetCard' | 'drawer' | 'dashControlWidget';
	export let group: Group;

	$: memberStatusStore = createMemberStatusSnapshot(group?.devices ?? []);
	$: memberSettingsStore = createMemberSettingsSnapshot(group?.devices ?? []);
	$: memberStatus = $memberStatusStore;
	$: memberSettings = $memberSettingsStore;

	$: allOffline = (group?.devices ?? []).every((deviceId) => {
		const status = memberStatus?.[real_id(deviceId)];
		return !status?.online;
	});

	$: noControlRights = !hasGroupControlRights(group, $devices);

	let cloud = {} as Device,
		// let local: any = {};//Not needed for trv
		disabled = {
			negative: false,
			positive: false,
		},
		unit,
		step,
		max,
		min,
		value_to_show,
		isLoading = false;
	let desired_temp = unit === 'C' ? 22 : 50;
	let onlineDevicesID = [];
	let hasWallDisplayThermostat = false;
	let pendingTemp: number | null = null;

	$: if (memberStatus || memberSettings || $user_settings) initialBaseValues(Object.keys(cloud));

	const separateTemperatures = (
		_devices: string[],
		desired: number = undefined,
	): {
		C: {
			devices: {};
			temperature: any;
		};
		F: {
			devices: {};
			temperature: any;
		};
	} => {
		const output = {
			C: { devices: {} as Device, temperature: undefined as number },
			F: { devices: {} as Device, temperature: undefined as number },
		};

		for (let index in _devices) {
			const id = _devices[index];
			const _device = $devices[id];
			const targetTemperature = getTemplateTargetTemp(_device);
			hasWallDisplayThermostat = isWallDisplay(_device?.type);

			if (targetTemperature) {
				const _unit = getTemplateTempUnit(_device);
				output[_unit].temperature =
					typeof desired !== 'undefined' ? desired : targetTemperature.status?.value;
				output[_unit].devices[id] = { ..._device };
			} else if (hasWallDisplayThermostat) {
				const realID = real_id(_device.id);
				const _unit = 'C';
				const temperature =
					typeof desired !== 'undefined'
						? desired
						: memberSettings?.[realID]?.['thermostat:0']?.target_C;
				output[_unit].temperature = temperature;
				output[_unit].devices[id] = { ..._device };
				min;
				max = 35;
			} else {
				const _unit = isBluTRV($devices[id]?.type)
					? 'C'
					: memberSettings?.[id]?.thermostats?.[_device.channel]?.target_t?.units;

				const temperature =
					typeof desired !== 'undefined'
						? desired
						: isBluTRV($devices[id]?.type)
							? getTrvComponentByDevice(_device)?.status['trv:0']?.target_C
							: memberSettings?.[id]?.thermostats?.[_device.channel]?.target_t?.value;

				if (typeof _unit !== 'undefined') {
					const isCalibrated = isBluTRV($devices[id]?.type)
						? isBluTRVCalibrated(_device)
						: memberStatus?.[real_id(id)]?.thermostats?.[0]?.pos !== -1;
					if (isCalibrated) {
						if (_unit === 'F' && temperature < 40) {
							output[_unit].temperature = +celsius_to_fahrenheit(temperature);
						} else if (_unit === 'C' && temperature > 31) {
							output[_unit].temperature = +fahrenheit_to_celsius(temperature).toFixed(0);
						} else {
							output[_unit].temperature =
								temperature ||
								memberSettings?.[id]?.thermostats?.[_device.channel]?.target_t?.value;
						}
					}
					output[_unit].devices[id] = { ...$devices?.[id] };
				}
			}
		}
		return output;
	};

	const initialBaseValues = (cloud) => {
		// cloud returns arr of id if all devices in the group
		const units = separateTemperatures(cloud);

		onlineDevicesID = cloud.filter((id) => {
			const isWallDisplayThermostat = isWallDisplay($devices[id]?.type);
			if (isWallDisplayThermostat) {
				return memberStatus[real_id(id)]?.online;
			} else {
				return memberStatus[real_id(id)]?.online || isBluTRV($devices[id]?.type);
			}
		}); // get online devices

		const onlineDevices = separateTemperatures(onlineDevicesID);

		let c = 0,
			f = 0;

		for (let unit in units) {
			for (let i = 0; i < Object.keys(units[unit].devices).length; i++) {
				unit === 'C' ? c++ : f++;
			}
		}

		const firstOnlineDevice = $devices[onlineDevicesID[0]];
		const targetTemperature = getTemplateTargetTemp(firstOnlineDevice);
		if (targetTemperature) {
			unit = getTemplateTempUnit(firstOnlineDevice);
		} else if (isWallDisplay(firstOnlineDevice?.type)) {
			unit = 'C';
		} else {
			unit = isBluTRV(firstOnlineDevice?.type)
				? 'C'
				: memberStatus?.[real_id(onlineDevicesID[0])]?.thermostats?.[firstOnlineDevice?.channel]
						?.target_t?.units; // use temp unit of the first online trv device
		}
		const deviceTemp = onlineDevices[unit]?.temperature;
		if (pendingTemp == null || pendingTemp === deviceTemp) {
			desired_temp = deviceTemp;
			pendingTemp = null;
		}
	};

	onMount(() => {
		// const lists = handleCloudLocalLists(group.devices);
		group.devices.map((dID) => {
			const device = $devices[dID];
			cloud[device?.id] = { ...device, id: dID };
		});
		initialBaseValues(Object.keys(cloud));
	});

	afterUpdate(() => {
		if (pendingTemp != null || isLoading) return;

		let available_devices =
			Object.keys(cloud)?.length === 0 ? {} : separateTemperatures(group.devices)[unit];
		if (
			typeof available_devices?.devices !== 'undefined' &&
			Object.keys(available_devices?.devices).length > 0 &&
			$req_locked_devices.length === Object.keys(available_devices?.devices).length
		) {
			//req pending so if the desired_temp !== undefined save its value
			desired_temp = available_devices.temperature;
		}

		// } else {
		if (
			typeof available_devices?.devices !== 'undefined' &&
			Object.keys(available_devices.devices).length < Object.keys(cloud)?.length
		) {
			//if we have at least one new socket status when we go from group in list into group page
			//need to update at the time with new value (it would be a desired temp value from the request)
			desired_temp = isBluTRV(available_devices[0]?.type)
				? 0
				: memberStatus[real_id(available_devices[0])]?.thermostats?.[0]?.target_t?.value ||
					desired_temp;
			// }
		}
	});

	beforeUpdate(() => {
		step = unit === 'C' ? 0.5 : unit === 'F' ? 1 : 10;

		max = step === 1 ? 87 : step === 0.5 ? 31 : 100;
		min = step === 1 ? 40 : step === 0.5 ? 4 : 0;

		if (unit === 'C' && desired_temp <= 4) {
			value_to_show = 'Lo';
		} else if (unit === 'C' && desired_temp >= 31) {
			value_to_show = 'Hi';
		} else if (unit === 'C' && desired_temp === 5) {
			value_to_show = 'fas fa-snowflake';
		} else if (unit === 'F' && desired_temp <= 40) {
			value_to_show = 'Lo';
		} else if (desired_temp >= 86) {
			value_to_show = 'Hi';
		} else {
			value_to_show = desired_temp + '°' + unit;
		}

		if (desired_temp <= min) {
			disabled = { negative: true, positive: false };
		} else if (desired_temp >= max) {
			disabled = { negative: false, positive: true };
		} else {
			disabled = { negative: false, positive: false };
		}
		if (hasWallDisplayThermostat) {
			// if we have wall display thermostat there are different limits and also we need to show the value no snowflake and Hi/Lo
			max = 35;
			min = 5;
			value_to_show = desired_temp + '°' + unit;
		}
	});

	const sendRequest = async () => {
		/** Before starts request => push all device ids in req_locked_devices store */
		addToRequestLockedList(cloud);
		const request = separateTemperatures(Object.keys(cloud), desired_temp);
		for (let unit of Object.keys(request)) {
			const unitDevices = Object.values(request[unit].devices) as Device[];
			const XT1Devices = unitDevices.filter((device) => getTemplateTargetTemp(device));
			const thermostatDevices = unitDevices.filter((device) => !getTemplateTargetTemp(device));

			if (thermostatDevices.length > 0) {
				await cloudGroupRequest(
					`/device/thermostat/bulk_control`,
					thermostatDevices.map((device: Device) => {
						if (isBluTRV(device.type)) {
							return {
								id: device.id.split('_')[0],
								channel: device.channel - 2000,
							};
						} else if (isWallDisplay(device.type)) {
							return {
								id: real_id(device.id),
								channel: 0,
								type: device.type,
							};
						} else {
							return {
								id: device.id,
								channel: device.channel,
							};
						}
					}),
					{
						temp: request[unit].temperature,
						unit: unit, //this will be supported on latest trv firmwares
					},
					24000,
				);
			}

			for (const device of XT1Devices) {
				const targetTemperature = getTemplateTargetTemp(device);
				if (targetTemperature?.config?.id != null) {
					await gen2Request(device, 'Number.set', {
						id: targetTemperature.config.id,
						value: request[unit].temperature,
					});
				}
			}
		}

		isLoading = false;
		disabled = { positive: false, negative: false };
		removeFromRequestLockedList(cloud);
	};

	/* time stuff */
	//This is needed not to spam server
	let timer;
	let interval;

	const setTime = (delay) => {
		clearTimeout(timer);
		timer = 0;
		timer = setTimeout(async () => {
			isLoading = true;
			await sendRequest();
		}, delay);
	};

	const resetTimeOut = () => {
		setTime(2200);
	};

	const clearButtonInterval = () => clearInterval(interval);

	const changeTempValue = (value) => {
		if ($os === 'IOS') {
			hapticFeedback('buttonPressed');
		}
		if (Math.abs(value) > 1) {
			desired_temp = value;
		} else {
			if (unit === 'C' && desired_temp === 5 && value < 0) {
				desired_temp = 4;
			} else if (unit === 'C' && desired_temp === 4 && value > 0) {
				desired_temp = 5;
			} else {
				desired_temp += value;
			}
		}
		// if (desired_temp < 0) {//This will not be needed because if device receive temperature value switches to auto temp control
		// 	desired_temp = 0;
		// } else if (desired_temp > 100) {
		// 	desired_temp = 100;
		// }

		if (desired_temp <= min) {
			desired_temp = min;
			clearButtonInterval();
		} else if (desired_temp >= max) {
			desired_temp = max;
			clearButtonInterval();
		}
		pendingTemp = desired_temp;
		resetTimeOut();
	};

	const handleLongPress = (val: number) => {
		interval = setInterval(() => {
			changeTempValue(val);
		}, 100);
	};

	$: heading =
		group.devices.length === 0
			? $format('alert_heading_group_control_no_device', {
					default: 'Controls are unavailable. No devices are in the group.',
				})
			: $format('alert_heading_group_control_offline_removed_device', {
					default:
						'Controls are unavailable. All devices are currently offline or have been removed..',
				});
</script>

{#if variant === 'drawer'}
	{#if allOffline}
		<Alert type="warning" class="mb-s" {heading} />
	{/if}
	<ControlsContainer color={$showNewLayout ? 'color-0' : undefined}>
		<svelte:fragment slot="first">
			<DoubleDeviceButton
				id="doubledevicebutton-35imax8"
				aria-label={$format('set_temperature', { default: 'Set Temperature' })}
				size="l"
				class="align-self-center"
				{isLoading}
				isActive={!isLoading}
				disabled={isLoading ||
					typeof desired_temp === 'undefined' ||
					allOffline ||
					noControlRights}
			>
				<DeviceButton
					id="devicebutton-9u0jyia"
					aria-label={$format('decrease_temperature', {
						default: 'Decrease Temperature',
					})}
					size="l"
					slot="buttonLeft"
					icon="fas fa-minus"
					variant="primary"
					disabled={disabled.negative ||
						isLoading ||
						groupSimilarFinder(group) ||
						noControlRights}
					on:touchstart={() => {
						changeTempValue(-step);
					}}
					on:click={() => {
						changeTempValue(-step);
					}}
					on:touchLongpress={() => handleLongPress(-step)}
					on:touchend={clearButtonInterval}
					on:mouseup={clearButtonInterval}
				/>
				<svelte:fragment slot="content">
					{#if value_to_show.includes('fas')}
						<Icon icon={value_to_show} />
					{:else if value_to_show.includes('Hi') || value_to_show.includes('Lo')}
						<Text styleType="controls-large" class="text-bold">
							{value_to_show}
						</Text>
					{:else if typeof desired_temp !== 'undefined'}
						<Text styleType="controls-large" class="text-bold">
							{desired_temp}
						</Text>
						<Text styleType="controls-small">{`°${unit}`}</Text>
					{:else}
						<Text styleType="controls-small">{`N/A`}</Text>
					{/if}
				</svelte:fragment>
				<DeviceButton
					aria-label={$format('increase_temperature', {
						default: 'Increase Temperature',
					})}
					id="devicebutton-tw5l6ft"
					size="l"
					slot="buttonRight"
					icon="fas fa-plus"
					variant="danger"
					disabled={disabled.positive ||
						isLoading ||
						groupSimilarFinder(group) ||
						noControlRights}
					on:touchstart={() => {
						changeTempValue(+step);
					}}
					on:click={() => {
						changeTempValue(+step);
					}}
					on:touchLongpress={() => handleLongPress(+step)}
					on:touchend={clearButtonInterval}
					on:mouseup={clearButtonInterval}
				/>
			</DoubleDeviceButton>
		</svelte:fragment>
	</ControlsContainer>

	<!-- <TemperatureWheel
		disabled={isLoading || groupSimilarFinder(group) || noControlRights}
		class="mb-l"
		value={desired_temp}
		valuePrefix={'°' + unit}
		{min}
		{max}
		type="temp"
		on:value_change={e => changeTempValue(e.detail.value)}
	/> -->
{:else if variant === 'widgetCard'}
	<DoubleDeviceButton
		id="doubledevicebutton-7wuhwei"
		aria-label={$format('set_temperature', { default: 'Set Temperature' })}
		size="m"
		class="align-self-center"
		{isLoading}
		isActive={!isLoading}
		disabled={isLoading || typeof desired_temp === 'undefined' || noControlRights}
	>
		<DeviceButton
			id="devicebutton-yicqv1o"
			aria-label={$format('decrease_temperature', {
				default: 'Decrease Temperature',
			})}
			size="m"
			slot="buttonLeft"
			icon="fas fa-minus"
			variant="primary"
			disabled={disabled.negative || isLoading || groupSimilarFinder(group) || noControlRights}
			on:touchstart={(e) => {
				e.stopPropagation();
				changeTempValue(-step);
			}}
			on:click={(e) => {
				e.stopPropagation();
				changeTempValue(-step);
			}}
			on:touchLongpress={() => handleLongPress(-step)}
			on:touchend={clearButtonInterval}
			on:mouseup={clearButtonInterval}
		/>
		<svelte:fragment slot="content">
			{#if value_to_show.includes('fas')}
				<Icon icon={value_to_show} />
			{:else if value_to_show.includes('Hi') || value_to_show.includes('Lo')}
				<Text styleType="controls-large" class="text-bold">
					{value_to_show}
				</Text>
			{:else if typeof desired_temp !== 'undefined'}
				<Text styleType="controls-large" class="text-bold">
					{desired_temp}
				</Text>
				<Text styleType="controls-small">{`°${unit}`}</Text>
			{:else}
				<Text styleType="controls-small">{`N/A`}</Text>
			{/if}
		</svelte:fragment>
		<DeviceButton
			aria-label={$format('increase_temperature', {
				default: 'Increase Temperature',
			})}
			id="devicebutton-3eiebmm"
			size="m"
			slot="buttonRight"
			icon="fas fa-plus"
			variant="danger"
			disabled={disabled.positive || isLoading || groupSimilarFinder(group) || noControlRights}
			on:touchstart={(e) => {
				e.stopPropagation();
				changeTempValue(+step);
			}}
			on:click={(e) => {
				e.stopPropagation();
				changeTempValue(+step);
			}}
			on:touchLongpress={() => handleLongPress(+step)}
			on:touchend={clearButtonInterval}
			on:mouseup={clearButtonInterval}
		/>
	</DoubleDeviceButton>
{:else if variant == 'dashControlWidget'}
	<div class="d-flex gap-s">
		<DeviceButtonV2
			size="s"
			aria-label={$format('increase_temperature', {
				default: 'Increase Temperature',
			})}
			id="devicebutton-3cnp93n"
			icon="fas fa-plus"
			variant="danger"
			inactiveIconColor="color-danger"
			{isLoading}
			isActive={false}
			disabled={disabled.positive ||
				isLoading ||
				groupSimilarFinder(group) ||
				noControlRights ||
				typeof desired_temp === 'undefined'}
			on:click={(event) => {
				event.stopPropagation();
				changeTempValue(+step);
			}}
		/>

		<DeviceButtonV2
			size="s"
			aria-label={$format('decrease_temperature', {
				default: 'Decrease Temperature',
			})}
			id="devicebutton-eapz38q"
			icon="fas fa-minus"
			variant="primary"
			{isLoading}
			isActive={false}
			disabled={disabled.negative ||
				isLoading ||
				groupSimilarFinder(group) ||
				noControlRights ||
				typeof desired_temp === 'undefined'}
			on:click={(event) => {
				event.stopPropagation();
				changeTempValue(-step);
			}}
		/>
	</div>
{:else if variant === 'listMenuCard' && $showNewLayout}
	<ActionsContainer
		orientation="vertical"
		slot="right"
		bgColor="color-base"
		type="separator"
		separatorColor="color-2"
	>
		<DeviceButtonV2
			aria-label={$format('decrease_temperature', {
				default: 'Decrease Temperature',
			})}
			id="devicebutton-eapz38q"
			icon="fa fa-minus"
			variant="primary"
			{isLoading}
			isActive={false}
			disabled={disabled.negative ||
				isLoading ||
				groupSimilarFinder(group) ||
				noControlRights ||
				typeof desired_temp === 'undefined'}
			slot="first"
			on:click={(event) => {
				event.stopPropagation();
				changeTempValue(-step);
			}}
		/>
		<DeviceButtonV2
			aria-label={$format('increase_temperature', {
				default: 'Increase Temperature',
			})}
			id="devicebutton-3cnp93n"
			icon="fa fa-plus"
			variant="danger"
			inactiveIconColor="color-danger"
			{isLoading}
			isActive={false}
			disabled={disabled.positive ||
				isLoading ||
				groupSimilarFinder(group) ||
				noControlRights ||
				typeof desired_temp === 'undefined'}
			slot="second"
			on:click={(event) => {
				event.stopPropagation();
				changeTempValue(+step);
			}}
		/>
	</ActionsContainer>
{:else}
	<ActionsContainer type={'separator'} orientation={'vertical'}>
		<DeviceButton
			aria-label={$format('decrease_temperature', {
				default: 'Decrease Temperature',
			})}
			id="devicebutton-eapz38q"
			icon="fa fa-minus"
			variant="primary"
			{isLoading}
			isActive={false}
			disabled={disabled.negative ||
				isLoading ||
				groupSimilarFinder(group) ||
				noControlRights ||
				typeof desired_temp === 'undefined'}
			slot="first"
			on:click={(event) => {
				event.stopPropagation();
				changeTempValue(-step);
			}}
		/>
		<DeviceButton
			aria-label={$format('increase_temperature', {
				default: 'Increase Temperature',
			})}
			id="devicebutton-3cnp93n"
			icon="fa fa-plus"
			variant="danger"
			{isLoading}
			isActive={false}
			disabled={disabled.positive ||
				isLoading ||
				groupSimilarFinder(group) ||
				noControlRights ||
				typeof desired_temp === 'undefined'}
			slot="second"
			on:click={(event) => {
				event.stopPropagation();
				changeTempValue(+step);
			}}
		/>
	</ActionsContainer>
{/if}
